import Button from 'ext-ui/components/Button/Button'
import InlineConfirmationPanel from 'ext-ui/components/InlineConfirmationPanel/InlineConfirmationPanel'
import React, { useEffect, useRef, useState } from 'react'
import SvgMore from 'src/components/Svgs/SvgMore'
import SvgTrash from 'src/components/Svgs/SvgTrash'
import MtAccountCollection from 'src/feature/MtAccountCollection/MtAccountCollection'
import { useTranslate } from 'src/localization/Trns'

import * as style from './PaymentInformationTab.style'
import { UseVendorPaymentMethodResult } from './useVendorPaymentMethod'
import VendorAchAccountCard from './VendorAchAccountCard'
import { footerError, pendingNote, visuallyHidden } from './VendorFlyout.style'

type TranslateFn = (key: string) => string

/**
 * The card's kebab → single destructive "Remove account" item, following the
 * WAI-ARIA menu-button pattern: `aria-haspopup`, `aria-expanded`, focus into
 * the item on open, Escape / outside-click close, focus returned to the trigger
 * on Escape. Opening the menu does nothing on its own — removal is gated behind
 * the confirm the item opens.
 */
const AccountActionsMenu: React.FC<{
  onRemove: () => void
  onReplace: () => void
  canReplace: boolean
  translate: TranslateFn
}> = ({ onRemove, onReplace, canReplace, translate }) => {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const itemRef = useRef<HTMLButtonElement | null>(null)
  const menuId = 'vendor-payment-actions-menu'

  useEffect(() => {
    if (!open) return undefined
    const handleMouseDown = (event: MouseEvent): void => {
      const target = event.target as Node
      if (
        !triggerRef.current?.contains(target) &&
        !menuRef.current?.contains(target)
      ) {
        setOpen(false)
      }
    }
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setOpen(false)
        triggerRef.current?.focus()
      }
    }
    document.addEventListener('mousedown', handleMouseDown)
    document.addEventListener('keydown', handleKeyDown)
    return (): void => {
      document.removeEventListener('mousedown', handleMouseDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open])

  useEffect(() => {
    if (open) itemRef.current?.focus()
  }, [open])

  return (
    <div css={style.menuWrap}>
      <button
        ref={triggerRef}
        type={'button'}
        css={style.kebabTrigger}
        aria-label={translate('Account actions')}
        aria-haspopup={'menu'}
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        data-testid={'vendor-payment-actions'}
        onClick={() => setOpen((value) => !value)}
      >
        <SvgMore width={20} height={20} viewBox={'0 0 24 24'} />
      </button>
      {open && (
        <div
          ref={menuRef}
          role={'menu'}
          id={menuId}
          css={style.menu}
          aria-label={translate('Account actions')}
        >
          {/* SPIKE: replace is listed first and is not destructive. Under
              retire-then-add there was no such action — replacing meant
              removing and then re-adding, with the vendor unpayable in
              between. */}
          {canReplace && (
            <button
              ref={itemRef}
              type={'button'}
              role={'menuitem'}
              css={style.menuItem}
              data-testid={'vendor-payment-replace'}
              onClick={() => {
                setOpen(false)
                // The item unmounts on close, so without this focus falls to
                // <body> and a keyboard user has to tab from the top of the
                // document to reach the confirm that just appeared below.
                triggerRef.current?.focus()
                onReplace()
              }}
            >
              {translate('Replace account')}
            </button>
          )}
          <button
            ref={canReplace ? undefined : itemRef}
            type={'button'}
            role={'menuitem'}
            css={[style.menuItem, style.menuItemSevere]}
            data-testid={'vendor-payment-remove'}
            onClick={() => {
              setOpen(false)
              triggerRef.current?.focus()
              onRemove()
            }}
          >
            <SvgTrash
              css={style.menuItemIcon}
              fill={'currentColor'}
              width={16}
              height={16}
              viewBox={'0 0 24 24'}
            />
            {translate('Remove account')}
          </button>
        </div>
      )}
    </div>
  )
}

/**
 * SPIKE. Shown for the whole of a replacement, in both the collect and the
 * acknowledge phases.
 *
 * It is doing real work, not decoration. Under retire-then-add the user arrives
 * at this embed with the vendor already unpayable and no way back; here the old
 * account is still current and still being paid from. That difference is
 * invisible unless it is stated, so it is stated.
 *
 * `onCancel` is passed ONLY at the collect stage, and the button renders only
 * when it is. Backing out is free there and nowhere else: an abandoned flow is
 * still `PENDING`, so a later Replace hits `FlowClaim.Relaunch` and transparently
 * reuses it.
 *
 * Past that point `completeFlowForEmbed` has run and the flow row is at
 * `AWAITING_ACKNOWLEDGEMENT`, where `CreateVendorAccountCollectionFlowCommand
 * .resolve()` returns `AcknowledgementRequired` — a 409 the tab renders as the
 * one-open-flow conflict, whose only affordance re-creates and 409s again. So a
 * cancel there is a one-way door: the user could never replace that account
 * again. Offering a button labelled "Keep current account" that walks into it
 * would be worse than offering nothing.
 *
 * Making it cancellable at that stage needs a real server-side flow cancel,
 * which is a much larger change than this carries.
 */
const ReplaceInProgressNote: React.FC<{
  translate: TranslateFn
  onCancel?: () => void
}> = ({ translate, onCancel }) => (
  <div css={style.sectionNote} data-testid={'vendor-payment-replacing-note'}>
    <p>
      {translate(
        'Enter the new ACH account below. This vendor is still active and payable on its current account, and stays that way until you confirm the new one.',
      )}
    </p>
    {onCancel && (
      <Button
        variant={'secondary'}
        type={'button'}
        onClick={onCancel}
        data-testid={'vendor-payment-replace-cancel'}
      >
        {translate('Keep current account')}
      </Button>
    )}
  </div>
)

export interface PaymentInformationTabProps {
  id: string
  labelledBy: string
  /**
   * Falsy when the tab is editable; otherwise WHY it is not. Read-only covers
   * two different situations that need the same gate but different copy — an
   * archived vendor, and a viewer without edit rights (EX-40761's `editEnabled`)
   * — so the reason travels with the gate rather than being re-derived here. A
   * plain boolean is what let the empty state tell a live vendor it was
   * archived.
   */
  readOnly: false | 'archived' | 'view-only'
  payment: UseVendorPaymentMethodResult
}

/**
 * The Payment Information subtab (EX-40772). States are selected off the vendor
 * detail — never by the bank read:
 *
 * - State A — a current ACH account: the summary card + kebab → confirm →
 *   retire. A provider read failure (502) stays State A with an error affordance
 *   and em-dashes, never the embed.
 * - State B — no current account: the reused Modern Treasury embed → link →
 *   (acknowledgement + Confirm and Add live in the shell footer).
 *
 * SPIKE: `payment.hasCurrentAccount` is no longer the SINGLE predicate. It was,
 * because a vendor could only ever be collecting an account while it had none.
 * The atomic swap adds a second dimension — `payment.isReplacing` — so a vendor
 * can be collecting a REPLACEMENT while its current account is still live and
 * still payable. The branch is `hasCurrentAccount && !isReplacing`.
 *
 * The ACH acknowledgement and its confirm action are contributed into the
 * shell's single shared footer (see VendorEditLoaded), not rendered here — this
 * tab never owns a footer of its own.
 */
const PaymentInformationTab: React.FC<PaymentInformationTabProps> = ({
  id,
  labelledBy,
  readOnly,
  payment,
}) => {
  const translate = useTranslate()

  return (
    <div
      css={style.tabBody}
      id={id}
      role={'tabpanel'}
      aria-labelledby={labelledBy}
      tabIndex={-1}
      data-testid={'vendor-edit-payment-tab'}
    >
      {payment.hasCurrentAccount && !payment.isReplacing ? (
        <>
          <VendorAchAccountCard
            details={payment.details}
            isLoading={payment.isDetailsLoading}
            isError={payment.isDetailsError}
            headerAction={
              !readOnly ? (
                <AccountActionsMenu
                  onRemove={payment.openRemoveConfirm}
                  onReplace={payment.openReplaceConfirm}
                  canReplace={payment.canReplace}
                  translate={translate}
                />
              ) : undefined
            }
          />

          {/* EX-40772's in-progress payment disclosure, in its generic form: a
              payment past MFA carries its own copy of the account it was
              authorized against and settles there regardless, so the user is
              told that plainly rather than being shown a per-payment amount +
              destination last-four (which would cost a payment-history call
              plus N detail reads at two Modern Treasury round-trips each).
              Pairs with the remove confirm, which covers the other half:
              payments still AWAITING approval are cancelled, not carried. */}
          <p
            css={style.sectionNote}
            data-testid={'vendor-payment-in-flight-note'}
          >
            {translate(
              "Any in-progress payments will continue to the vendor's account at the time the payment was authorized.",
            )}
          </p>

          {payment.removeError && (
            <p css={footerError} role={'alert'}>
              {payment.removeError}
            </p>
          )}

          {payment.isConfirmingRemoval && (
            // The panel carries a material consequence (it also cancels
            // payments awaiting approval), and ext-ui's InlineConfirmationPanel
            // ships no role of its own, so announce it where it is used.
            <div role={'alert'}>
              <InlineConfirmationPanel
                message={translate(
                  "Remove ACH details? This vendor's ACH account details will be cleared and any payment to this vendor still waiting for approval will be cancelled. You'll need to re-enter the details to add a payment method again.",
                )}
                confirmLabel={translate('Remove')}
                onCancel={payment.cancelRemoveConfirm}
                onConfirm={payment.confirmRemoval}
              />
            </div>
          )}

          {payment.isConfirmingReplace && (
            // SPIKE: the disclosure sits HERE, at the point of consent, rather
            // than after the user has filled in a bank form. A swap cancels
            // payments awaiting approval exactly as removal does, so the user
            // has to be told before they start, not after. This placement is
            // the whole reason the spike's cancellation-timing decision is
            // defensible.
            <div role={'alert'}>
              <InlineConfirmationPanel
                message={translate(
                  "Replace ACH details? This vendor stays payable on its current account until you confirm the new one, and nothing changes if you don't finish. Any payment to this vendor still waiting for approval will be cancelled when the new account is confirmed.",
                )}
                confirmLabel={translate('Continue')}
                onCancel={payment.cancelReplaceConfirm}
                onConfirm={payment.confirmReplace}
              />
            </div>
          )}
        </>
      ) : readOnly ? (
        // Read-only vendor with no current account. The shell hides the footer
        // here, so rendering the collection embed would walk the user through
        // creating a real external account at Modern Treasury with no Confirm
        // and Add to finish it — a dead end that leaves an orphaned account
        // behind. Read-only means read-only, whatever the reason.
        <p
          css={style.sectionNote}
          data-testid={
            readOnly === 'archived'
              ? 'vendor-payment-archived-note'
              : 'vendor-payment-view-only-note'
          }
        >
          {readOnly === 'archived'
            ? translate(
                'This vendor is archived. Restore it to add ACH payment details.',
              )
            : translate(
                'You do not have permission to change ACH payment details for this vendor.',
              )}
        </p>
      ) : payment.hasOpenFlowConflict ? (
        // One-open-flow: a just-retired vendor still held a stale open flow, so
        // starting a new one 409'd. Offer a retry, not a dead end.
        <div data-testid={'vendor-payment-flow-conflict'}>
          <p css={footerError} role={'alert'}>
            {translate(
              "We couldn't start a new bank account setup because an earlier one is still open. Please try again.",
            )}
          </p>
          <Button
            variant={'secondary'}
            type={'button'}
            onClick={payment.retryReAdd}
            data-testid={'vendor-payment-flow-retry'}
          >
            {translate('Try again')}
          </Button>
        </div>
      ) : payment.phase === 'collecting' ? (
        <>
          {payment.isReplacing ? (
            <ReplaceInProgressNote
              translate={translate}
              onCancel={payment.cancelReplace}
            />
          ) : (
            <p css={style.sectionNote}>
              {translate('Add a new ACH account for this vendor.')}
            </p>
          )}
          <MtAccountCollection
            key={payment.embedKey}
            createFlow={payment.createFlowForEmbed}
            completeFlow={payment.completeFlowForEmbed}
            onLinked={payment.handleCollectionLinked}
            showHeading={false}
            flush
          />
        </>
      ) : (
        // Phase B — Modern Treasury accepted the hosted form. The acknowledgement
        // checkbox + Confirm and Add live in the shell footer; this body carries
        // only the status / error surfaces for the confirm sequence.
        <div role={'group'} aria-label={translate('Confirm ACH account')}>
          {/* The replace note leads, so the card below reads as "the account you
              are about to switch to" rather than as the account in use. */}
          {/* No onCancel past the collect stage -- see ReplaceInProgressNote. */}
          {payment.isReplacing && (
            <ReplaceInProgressNote translate={translate} />
          )}
          <VendorAchAccountCard
            details={payment.pendingDetails}
            isLoading={payment.isPendingDetailsLoading}
            isError={payment.isPendingDetailsError}
            testId={'vendor-pending-account-card'}
          />

          {payment.phase === 'confirmation-pending' && (
            <p css={pendingNote} role={'status'} aria-live={'polite'}>
              {payment.confirmation.canRetry
                ? translate(
                    'This bank account is still being confirmed. Try again to check its status.',
                  )
                : translate(
                    'This bank account is still being confirmed. This can take a moment.',
                  )}
            </p>
          )}
          {payment.phase === 'confirmation-error' &&
            payment.confirmationErrorMessage && (
              <p css={footerError} role={'alert'}>
                {payment.confirmationErrorMessage}
              </p>
            )}
          {payment.phase === 'confirming' && (
            <p css={visuallyHidden} role={'status'} aria-live={'polite'}>
              {translate('Updating payment details…')}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

export default PaymentInformationTab
